import { useCallback, useEffect, useState } from "react";

/**
 * Collapsed/expanded state of the desktop shell sidebar.
 *
 * Two inputs decide it:
 * - the operator's own choice, a per-browser display preference persisted
 *   the same way as the active shell (`sentinels_active_shell`) and the
 *   language: in localStorage, so it survives reloads and logouts without a
 *   backend field;
 * - focus mode: on a work surface (see `isFocusRoute`) the sidebar steps
 *   aside on its own so the page gets the width. Re-opening it there holds
 *   for that visit only; leaving the work surface restores the preference
 *   and the next one collapses again.
 */
export const SIDEBAR_COLLAPSED_STORAGE_KEY = "sentinels_sidebar_collapsed";

/** A project's workspace in Frontier and Kodiak: planner, formulation, … */
const FOCUS_ROUTE = /^\/(ops|client)\/projects\/[^/]+/;

export function isFocusRoute(pathname: string): boolean {
  return FOCUS_ROUTE.test(pathname);
}

/**
 * ⌘B on Apple platforms, Ctrl+B elsewhere. Only one modifier per platform:
 * on macOS Ctrl+B moves the caret back one character in text fields.
 */
function isApplePlatform(): boolean {
  return (
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad/.test(navigator.userAgent)
  );
}

/** Shortcut as shown in the toggle's tooltip. */
export const SIDEBAR_SHORTCUT_LABEL = isApplePlatform() ? "⌘B" : "Ctrl+B";

/** Shortcut in `aria-keyshortcuts` syntax. */
export const SIDEBAR_SHORTCUT_ARIA = isApplePlatform() ? "Meta+B" : "Control+B";

function readStoredCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
}

/**
 * @param shortcutEnabled - registers ⌘B (Apple) / Ctrl+B while true. The mobile
 *   drawer has no collapsed mode, so the layout turns it off there.
 * @param focusRoute - the current page is a work surface (focus mode).
 */
export function useSidebarCollapsed(
  shortcutEnabled = true,
  focusRoute = false,
) {
  const [pinnedCollapsed, setPinnedCollapsed] = useState(readStoredCollapsed);
  // The operator re-opened the sidebar on the current work surface.
  const [focusExpanded, setFocusExpanded] = useState(false);
  const [previousFocusRoute, setPreviousFocusRoute] = useState(focusRoute);

  if (focusRoute !== previousFocusRoute) {
    setPreviousFocusRoute(focusRoute);
    if (!focusRoute) setFocusExpanded(false);
  }

  const collapsed = pinnedCollapsed || (focusRoute && !focusExpanded);

  useEffect(() => {
    localStorage.setItem(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      String(pinnedCollapsed),
    );
  }, [pinnedCollapsed]);

  const toggleCollapsed = useCallback(() => {
    if (collapsed) {
      setPinnedCollapsed(false);
      if (focusRoute) setFocusExpanded(true);
    } else if (focusRoute) {
      // Back to the work surface's default, without changing the preference.
      setFocusExpanded(false);
    } else {
      setPinnedCollapsed(true);
    }
  }, [collapsed, focusRoute]);

  useEffect(() => {
    if (!shortcutEnabled) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== "b") return;
      const modifier = isApplePlatform()
        ? event.metaKey && !event.ctrlKey
        : event.ctrlKey && !event.metaKey;
      if (!modifier || event.altKey || event.shiftKey) return;
      // Rich-text editors own ⌘B (bold).
      if (event.target instanceof HTMLElement && event.target.isContentEditable)
        return;
      event.preventDefault();
      // Holding the keys would flicker the rail.
      if (event.repeat) return;
      toggleCollapsed();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [shortcutEnabled, toggleCollapsed]);

  return { collapsed, toggleCollapsed };
}
